import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useEdgesState,
  useNodesState,
  type Viewport,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { cx, EmptyState } from '@wapharma/design-system';
import styles from './ProductTypeTaxonomyGraph.module.css';
import { buildLayout, computeEmphasis } from './productTypeTaxonomyGraphLayout';
import { TAXONOMY_GRAPH_NODE_TYPES } from './ProductTypeTaxonomyGraphNodes';

/* ──────────────────────────────────────────────────────────────────────────
 * Public types — kept inline so the page importing the graph stays simple.
 * The graph owns layout + emphasis logic; the page owns data fetching.
 * ────────────────────────────────────────────────────────────────────────── */

export type ProductTypeRow = {
  code: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  imageKey?: string | null;
  imageUrl?: string | null;
  roots: { id: string; categoryId: string; name: string; slug: string }[];
};

export type CategoryRow = {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  imageKey?: string | null;
  imageUrl?: string | null;
};

export type ProductTypeTaxonomyGraphProps = {
  productTypes: ProductTypeRow[];
  categories: CategoryRow[];
  selectedTypeCode: string;
  canEdit: boolean;
  token: string;
  /** Called after a successful inline mutation so the parent can reload data. */
  onChange: () => void | Promise<void>;
  /** Optional click on a non-selected type card → switch context to that type. */
  onSelectType?: (code: string) => void;
  /** Smaller canvas for onboarding dock — no legend/minimap. */
  compact?: boolean;
  /**
   * Fit the selected branch inside the canvas whenever the box is measured.
   * Use when the graph mounts inside a popover that has no size on first paint.
   */
  frameContent?: boolean;
  /**
   * Seed Installer: cycle Pexels preview on thumb.
   * seedKey = type code or category slug. Returns new imageUrl (or null).
   */
  onCycleSeedPhoto?: (seedKey: string) => void | Promise<string | null>;
  className?: string;
};

type EmphasisLevel = 'selected' | 'on-path' | 'muted' | 'neutral' | 'orphan';

type TypeNodeData = {
  kind: 'type';
  type: ProductTypeRow;
  /** All root categories (id+name+slug) — used by the inline root selector. */
  rootOptions: CategoryRow[];
  emphasis: EmphasisLevel;
  selected: boolean;
  canEdit: boolean;
  minimal: boolean;
  token: string;
  onChange: () => void | Promise<void>;
  onSelectType?: (code: string) => void;
  onCycleSeedPhoto?: (seedKey: string) => void | Promise<string | null>;
  vertical?: boolean;
};

type CategoryNodeData = {
  kind: 'category';
  category: CategoryRow;
  isRoot: boolean;
  hasChildren: boolean;
  childCount: number;
  ownerTypes: ProductTypeRow[];
  emphasis: EmphasisLevel;
  canEdit: boolean;
  minimal: boolean;
  token: string;
  onChange: () => void | Promise<void>;
  onCycleSeedPhoto?: (seedKey: string) => void | Promise<string | null>;
  vertical?: boolean;
};

type GraphNode = Node<TypeNodeData | CategoryNodeData>;

/* ──────────────────────────────────────────────────────────────────────────
 * Graph container
 * ────────────────────────────────────────────────────────────────────────── */

function ProductTypeTaxonomyGraphInner({
  productTypes,
  categories,
  selectedTypeCode,
  canEdit,
  token,
  onChange,
  onSelectType,
  onCycleSeedPhoto,
  compact = false,
  frameContent = false,
  className,
}: ProductTypeTaxonomyGraphProps) {
  const reactFlow = useReactFlow<GraphNode, Edge>();
  const canvasRef = useRef<HTMLDivElement>(null);
  const fittedSizeRef = useRef('');

  // Stable callback wrappers — parent arrows must not rebuild React Flow nodes.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onSelectTypeRef = useRef(onSelectType);
  onSelectTypeRef.current = onSelectType;
  const onCycleSeedPhotoRef = useRef(onCycleSeedPhoto);
  onCycleSeedPhotoRef.current = onCycleSeedPhoto;
  const stableOnChange = useCallback(() => onChangeRef.current(), []);
  const stableOnSelectType = useCallback((code: string) => onSelectTypeRef.current?.(code), []);
  const stableOnCycleSeedPhoto = useCallback(
    (seedKey: string) => onCycleSeedPhotoRef.current?.(seedKey),
    [],
  );
  const childrenByParent = useMemo(() => {
    const m = new Map<string, CategoryRow[]>();
    for (const c of categories) {
      if (!c.parentId) continue;
      const arr = m.get(c.parentId) ?? [];
      arr.push(c);
      m.set(c.parentId, arr);
    }
    for (const [, arr] of m) arr.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
    return m;
  }, [categories]);

  const rootOptions = useMemo(
    () => categories.filter((c) => c.parentId == null).sort((a, b) => a.name.localeCompare(b.name, 'fr')),
    [categories],
  );

  const { ownersByCategory, onPathCategories } = useMemo(
    () => computeEmphasis(productTypes, childrenByParent, selectedTypeCode),
    [productTypes, childrenByParent, selectedTypeCode],
  );

  const { positions, typeKey, metrics } = useMemo(
    () => buildLayout(productTypes, categories, childrenByParent, compact, frameContent),
    [productTypes, categories, childrenByParent, compact, frameContent],
  );

  // ── Build React Flow nodes + edges from the layout/emphasis indices.
  const { initialNodes, initialEdges } = useMemo(() => {
    const flowNodes: GraphNode[] = [];
    const flowEdges: Edge[] = [];

    for (const t of productTypes) {
      const pos = positions.get(typeKey(t.code));
      if (!pos) continue;
      const isSelected = t.code === selectedTypeCode;
      const emphasis: EmphasisLevel = isSelected ? 'selected' : 'muted';
      flowNodes.push({
        id: typeKey(t.code),
        type: 'type',
        position: { x: pos.x, y: pos.y },
        data: {
          kind: 'type',
          type: t,
          rootOptions,
          emphasis,
          selected: isSelected,
          canEdit: compact ? false : canEdit,
          minimal: compact,
          vertical: frameContent,
          token,
          onChange: stableOnChange,
          onSelectType: onSelectType ? stableOnSelectType : undefined,
          onCycleSeedPhoto: onCycleSeedPhoto ? stableOnCycleSeedPhoto : undefined,
        },
        draggable: !compact,
        selectable: false,
        style: { width: metrics.nodeWidth },
      });

      // Edges from type → its root categories.
      for (const r of t.roots) {
        const onPath = onPathCategories.has(r.categoryId);
        flowEdges.push({
          id: `e-${typeKey(t.code)}-${r.categoryId}`,
          source: typeKey(t.code),
          target: r.categoryId,
          sourceHandle: 'out',
          targetHandle: 'in',
          type: 'smoothstep',
          className: onPath ? 'edge-on-path' : 'edge-muted',
        });
      }
    }

    for (const c of categories) {
      const pos = positions.get(c.id);
      if (!pos) continue;
      const owners = ownersByCategory.get(c.id);
      const ownerCodes = owners ? Array.from(owners) : [];
      const ownerTypes = ownerCodes
        .map((code) => productTypes.find((t) => t.code === code))
        .filter((t): t is ProductTypeRow => Boolean(t));
      const isOnPath = onPathCategories.has(c.id);
      const emphasis: EmphasisLevel = isOnPath
        ? 'on-path'
        : ownerCodes.length > 0
          ? 'muted'
          : 'orphan';
      const childCount = (childrenByParent.get(c.id) ?? []).length;
      const isRoot = c.parentId == null;

      flowNodes.push({
        id: c.id,
        type: 'category',
        position: { x: pos.x, y: pos.y },
        data: {
          kind: 'category',
          category: c,
          isRoot,
          hasChildren: childCount > 0,
          childCount,
          ownerTypes,
          emphasis,
          canEdit: compact ? false : canEdit,
          minimal: compact,
          vertical: frameContent,
          token,
          onChange: stableOnChange,
          onCycleSeedPhoto: onCycleSeedPhoto ? stableOnCycleSeedPhoto : undefined,
        },
        draggable: !compact,
        selectable: false,
        style: { width: metrics.nodeWidth },
      });

      // Edge from parent category → this child.
      if (c.parentId) {
        const parentOnPath = onPathCategories.has(c.parentId);
        const childOnPath = onPathCategories.has(c.id);
        const edgeOnPath = parentOnPath && childOnPath;
        flowEdges.push({
          id: `e-${c.parentId}-${c.id}`,
          source: c.parentId,
          target: c.id,
          sourceHandle: 'out',
          targetHandle: 'in',
          type: 'smoothstep',
          className: edgeOnPath ? 'edge-on-path' : 'edge-muted',
        });
      }
    }

    return { initialNodes: flowNodes, initialEdges: flowEdges };
  }, [
    productTypes,
    categories,
    positions,
    typeKey,
    metrics,
    compact,
    selectedTypeCode,
    onPathCategories,
    ownersByCategory,
    childrenByParent,
    rootOptions,
    canEdit,
    token,
    stableOnChange,
    stableOnSelectType,
    stableOnCycleSeedPhoto,
    onSelectType,
    onCycleSeedPhoto,
  ]);

  // Controlled state so React Flow drag updates persist within the session.
  const [nodes, setNodes, onNodesChange] = useNodesState<GraphNode>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges);

  // Keep node/edge state synced when upstream data refreshes (selected type
  // changes, categories edited, etc.). Preserve existing node positions so
  // user drags aren't undone on each parent re-render.
  const positionRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const viewportRef = useRef<Viewport | null>(null);
  const didInitialFitRef = useRef(false);
  const prevSelectedTypeCodeRef = useRef<string | null>(null);
  const pendingAutoFocusRef = useRef(true);
  useEffect(() => {
    for (const n of nodes) positionRef.current.set(n.id, n.position);
  }, [nodes]);

  useEffect(() => {
    if (prevSelectedTypeCodeRef.current !== selectedTypeCode) {
      prevSelectedTypeCodeRef.current = selectedTypeCode;
      pendingAutoFocusRef.current = true;
    }
  }, [selectedTypeCode]);

  useEffect(() => {
    setNodes(
      initialNodes.map((n) => {
        const stored = positionRef.current.get(n.id);
        return stored ? { ...n, position: stored } : n;
      }),
    );
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Keep current pan/zoom when nodes/edges refresh after inline edits.
  // Compact setup: always frame the selected type branch (never whole forest).
  useEffect(() => {
    if (nodes.length === 0) return;

    const focusIds = new Set<string>([
      typeKey(selectedTypeCode),
      ...Array.from(onPathCategories),
    ]);
    const focusNodes = nodes.filter((n) => focusIds.has(n.id));
    const fitOpts = frameContent
      ? { padding: 0.2, maxZoom: 1.05, minZoom: 0.12, duration: 0 as const }
      : compact
        ? { padding: 0.14, maxZoom: 1.35, minZoom: 0.7, duration: 0 as const }
        : { padding: 0.28, maxZoom: 1.2, minZoom: 0.35, duration: 0 as const };

    if (pendingAutoFocusRef.current && focusNodes.length > 0) {
      didInitialFitRef.current = true;
      pendingAutoFocusRef.current = false;
      reactFlow.fitView({ nodes: focusNodes, ...fitOpts });
      requestAnimationFrame(() => {
        viewportRef.current = reactFlow.getViewport();
      });
      return;
    }

    if (compact && focusNodes.length > 0 && !didInitialFitRef.current) {
      didInitialFitRef.current = true;
      reactFlow.fitView({ nodes: focusNodes, ...fitOpts });
      requestAnimationFrame(() => {
        viewportRef.current = reactFlow.getViewport();
      });
      return;
    }

    const saved = viewportRef.current;
    if (saved) {
      reactFlow.setViewport(saved, { duration: 0 });
      return;
    }
    if (!didInitialFitRef.current) {
      didInitialFitRef.current = true;
      if (focusNodes.length > 0) {
        reactFlow.fitView({ nodes: focusNodes, ...fitOpts });
      } else {
        reactFlow.fitView({
          padding: compact ? 0.14 : 0.2,
          maxZoom: compact ? 1.35 : 1,
          minZoom: compact ? 0.7 : 0.3,
          duration: 0,
        });
      }
      requestAnimationFrame(() => {
        viewportRef.current = reactFlow.getViewport();
      });
    }
  }, [reactFlow, nodes, edges, selectedTypeCode, onPathCategories, typeKey, compact, frameContent]);

  useEffect(() => {
    if (!frameContent) return;
    const el = canvasRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const fit = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w < 8 || h < 8) return;
      const key = `${w}x${h}:${nodes.length}`;
      if (fittedSizeRef.current === key) return;
      fittedSizeRef.current = key;
      const focusIds = new Set<string>([
        typeKey(selectedTypeCode),
        ...Array.from(onPathCategories),
      ]);
      const focusNodes = nodes.filter((n) => focusIds.has(n.id));
      reactFlow.fitView({
        nodes: focusNodes.length > 0 ? focusNodes : undefined,
        padding: 0.22,
        maxZoom: 1.05,
        minZoom: 0.12,
        duration: 0,
      });
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [frameContent, reactFlow, nodes, selectedTypeCode, onPathCategories, typeKey]);

  const proOptions = useMemo(() => ({ hideAttribution: true }), []);
  const nodeTypes = useMemo(() => TAXONOMY_GRAPH_NODE_TYPES, []);

  if (productTypes.length === 0 && categories.length === 0) {
    return <EmptyState title="Aucun type ou catégorie à afficher." />;
  }

  return (
    <div ref={canvasRef} className={cx(styles.canvas, compact && styles.canvasCompact, className)}>
      {compact ? null : <Legend />}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodesDraggable={!compact}
        nodesConnectable={false}
        elementsSelectable={false}
        /** Left-drag pans canvas; node cards use nopan for photo cycle / clicks. */
        panOnDrag
        panOnScroll
        zoomOnScroll
        zoomOnPinch
        minZoom={frameContent ? 0.12 : compact ? 0.55 : 0.2}
        maxZoom={1.4}
        proOptions={proOptions}
        onMoveEnd={(_, viewport) => {
          viewportRef.current = viewport;
        }}
      >
        <Background variant={BackgroundVariant.Dots} gap={18} size={1} color="color-mix(in oklab, var(--wp-feldspar-500) 22%, transparent)" />
        <Controls showInteractive={false} />
        {compact ? null : (
          <MiniMap
            pannable
            zoomable
            nodeColor={(n) => {
              const emphasis = (n.data as TypeNodeData | CategoryNodeData | undefined)?.emphasis;
              if (emphasis === 'selected') return 'var(--wp-brunswick-600)';
              if (emphasis === 'on-path') return 'var(--wp-menthol-400)';
              if (emphasis === 'muted') return 'var(--wp-feldspar-400)';
              if (emphasis === 'orphan') return 'var(--wp-warning)';
              return 'var(--wp-feldspar-300)';
            }}
            nodeStrokeWidth={2}
          />
        )}
      </ReactFlow>
    </div>
  );
}

function Legend(): ReactNode {
  const items: Array<{ label: string; swatchClass: string }> = [
    { label: 'Type sélectionné', swatchClass: styles.legendSwatchSelected },
    { label: 'Sur le chemin', swatchClass: styles.legendSwatchOnPath },
    { label: 'Autre type', swatchClass: styles.legendSwatchMuted },
    { label: 'Non assigné', swatchClass: styles.legendSwatchOrphan },
  ];
  return (
    <div className={styles.legend}>
      {items.map((it) => (
        <span key={it.label} className={styles.legendItem}>
          <span className={cx(styles.legendSwatch, it.swatchClass)} aria-hidden />
          <span>{it.label}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Card-tree graph for the product type detail page.
 * Renders the full taxonomy as an interactive React Flow canvas where the
 * selected type's branch is emphasized and other branches are muted.
 *
 * Responsibilities:
 *  - Layout (left-to-right tree based on subtree heights).
 *  - Emphasis classification (selected / on-path / muted / orphan).
 *  - Inline edit + delete actions on each node card.
 *
 * Data fetching is delegated to the parent — call `onChange` after a save to
 * trigger a fresh load (categories/product-type roots may have shifted).
 */
export function ProductTypeTaxonomyGraph(props: ProductTypeTaxonomyGraphProps) {
  return (
    <ReactFlowProvider>
      <ProductTypeTaxonomyGraphInner {...props} />
    </ReactFlowProvider>
  );
}
